<template>
  <div class="terminal-console" :class="{ 'terminal-console--collapsed': isCollapsed }">
    <div class="terminal-console__header" v-show="!isCollapsed">
      <div class="terminal-console__title">
        <i class="fa fa-terminal"></i>
        <span>Terminal</span>
      </div>
      <button class="terminal-console__collapse-btn" @click.stop="toggleCollapse">
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
    
    <div class="terminal-console__content" v-show="!isCollapsed">
      <div class="terminal-console__toolbar">
        <button class="btn btn-sm btn-outline-secondary" @click="clearConsole">
          <i class="fa fa-trash"></i> Clear
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="copyToClipboard">
          <i class="fa fa-copy"></i> Copy
        </button>
      </div>
      
      <div class="terminal-console__output" ref="consoleOutput">
        <div class="terminal-console__prompt">
          <span class="terminal-console__prompt-symbol">$</span>
          <span class="terminal-console__prompt-text">Ready</span>
        </div>
        <div v-for="(log, index) in logs" :key="index" 
             :class="['terminal-console__line', `terminal-console__line--${log.type}`]">
          <span class="terminal-console__timestamp">{{ log.timestamp }}</span>
          <span class="terminal-console__content">{{ log.message }}</span>
        </div>
      </div>
    </div>

    <button v-show="isCollapsed" class="terminal-console__collapse-btn" @click.stop="toggleCollapse">
      <i class="fa fa-chevron-left"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: 'TerminalConsole',
  props: {
    logs: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isCollapsed: false
    };
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('collapse', this.isCollapsed);
    },
    clearConsole() {
      this.$emit('clear');
    },
    async copyToClipboard() {
      try {
        const text = this.logs.map(log => 
          `[${log.timestamp}] ${log.message}`
        ).join('\n');
        await navigator.clipboard.writeText(text);
        this.$emit('log', { message: 'Console output copied to clipboard', type: 'success' });
      } catch (error) {
        this.$emit('log', { message: 'Failed to copy to clipboard', type: 'error' });
      }
    }
  },
  watch: {
    logs: {
      handler() {
        this.$nextTick(() => {
          const consoleOutput = this.$refs.consoleOutput;
          if (consoleOutput) {
            consoleOutput.scrollTop = consoleOutput.scrollHeight;
          }
        });
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.terminal-console {
  background: #1e1e1e;
  border-radius: 10px;
  height: calc(100vh - 200px);
  transition: all 0.3s ease;
  font-family: 'Fira Code', 'Consolas', monospace;
}

.terminal-console--collapsed {
  width: 48px;
  background: transparent;
  border: none;
}

.terminal-console__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #2d2d2d;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.terminal-console__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 14px;
}

.terminal-console__title i {
  color: #00ff00;
}

.terminal-console__content {
  height: calc(100% - 45px);
  display: flex;
  flex-direction: column;
}

.terminal-console__toolbar {
  padding: 8px;
  border-bottom: 1px solid #424351;
  display: flex;
  gap: 8px;
}

.terminal-console__output {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  font-size: 13px;
  color: #fff;
  background: #1e1e1e;
}

.terminal-console__prompt {
  margin-bottom: 10px;
  color: #00ff00;
}

.terminal-console__prompt-symbol {
  margin-right: 8px;
}

.terminal-console__line {
  margin-bottom: 5px;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Fira Code', 'Consolas', monospace;
}

.terminal-console__timestamp {
  color: #6c757d;
  margin-right: 8px;
  font-size: 12px;
}

.terminal-console__line--error {
  color: #ff5555;
}

.terminal-console__line--success {
  color: #50fa7b;
}

.terminal-console__line--info {
  color: #8be9fd;
}

.terminal-console__line--data {
  color: #ffb86c;
  background: rgba(255, 184, 108, 0.1);
  padding: 5px;
  border-radius: 4px;
  margin: 5px 0;
}

.btn-link {
  color: #fff;
  text-decoration: none;
}

.btn-link:hover {
  color: #00ff00;
}

.btn-outline-secondary {
  color: #fff;
  border-color: #424351;
  background: transparent;
}

.btn-outline-secondary:hover {
  background: #424351;
  color: #fff;
}

/* Custom scrollbar */
.terminal-console__output::-webkit-scrollbar {
  width: 8px;
}

.terminal-console__output::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.terminal-console__output::-webkit-scrollbar-thumb {
  background: #424351;
  border-radius: 4px;
}

.terminal-console__output::-webkit-scrollbar-thumb:hover {
  background: #4f4f5f;
}

.terminal-console__collapse-btn {
  padding: 8px 12px;
  border: 2px solid #424351;
  background: #2d2d2d;
  color: #fff;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 16px;
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.terminal-console__collapse-btn:hover {
  background: #424351;
  border-color: #00ff00;
  color: #00ff00;
  transform: scale(1.05);
}

.terminal-console--collapsed .terminal-console__collapse-btn {
  position: fixed;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #2d2d2d;
  border: 2px solid #424351;
  padding: 12px 8px;
  border-radius: 6px;
  z-index: 1000;
  min-width: 48px;
  height: 48px;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.terminal-console--collapsed .terminal-console__collapse-btn:hover {
  background: #424351;
  border-color: #00ff00;
  color: #00ff00;
  transform: translateY(-50%) scale(1.05);
}
</style> 